import {
  JSXElementConstructor,
  MutableRefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, SxProps, Theme } from "@mui/material";
import {
  DataGridPremium,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_FIELD,
  GridApi,
  GridCallbackDetails,
  GridColDef,
  GridEventListener,
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  GridRowSelectionModel,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
  gridFilterModelSelector,
  useGridApiRef,
} from "@mui/x-data-grid-premium";


import LoadingSpinner from "../Loading";
import { PremiumListToolbar } from "./PremiumListToolbar";
import "./datagrid.css";

import type {
  ActionMenuItem,
  ActionMenuEntry,
  ActionMenuSubheader,
} from "./ActionMenu";
import type { CreateMenuSection } from "./CreateMenu";
import type { AdvancedSearchFormProps } from "./AdvancedSearch";
import { AdvancedSearch2, AdvancedSearchForm2 } from "./AdvanceSearchUpdate";
// TODO: Detail-view grids should not persist filters, sort, or display
// preferences. Re-enable this import with the save/load calls below when
// AeronetDataGridPremium is used on list screens.
// import FilterApi, {
//   FilterDelete,
//   FilterGetApi,
//   UserDisplayPreference,
//   UserDisplayPreferenceGet,
//   UserSortPreference,
//   UserSortPreferenceGet,
// } from "./filterApi";
import { useDataGridStore } from "../../store/DataGrid";
import { commonDataGridPremiumSx, detailTabGridSx, listingPremiumGridSx } from "../../utils/dataGridSx";
import { FILTER_PANEL_PROPS } from "./filterPanel";
import CustomFilterPanelPremium from "../CustomFilterPanelPremium";
import { isAppliedFilterItem } from "./serverFilter";
import { Actions, type ActionType } from "./DataGrid";

export interface QueryResults<T> {
  total: number;
  rows: T[];
}

export interface AdvancedSearchProps {
  onSearch?: (
    model: GridFilterModel,
    apiRef: MutableRefObject<GridApi>
  ) => void;
  items?: GridFilterItem[];
  [x: string | number | symbol]: unknown;
}

export interface AeronetDataGridPremiumProps<T extends GridValidRowModel> {
  actions?: ActionMenuEntry[];
  advancedSearchForm?: JSXElementConstructor<AdvancedSearchFormProps>;
  advancedSearchProps?: AdvancedSearchProps;
  checkboxSelection?: boolean;
  children?: (props: {
    onClose: () => void;
    action: ActionType;
    Actions: { [key: string]: ActionType };
  }) => React.ReactNode;
  columns: GridColDef<T>[];
  components?: {
    toolbar?: JSXElementConstructor<any>;
    [key: string]: any;
  };
  currentUserId: number;
  data: (
    params: { [key: string]: any },
    config?: { [key: string]: any },
    apiRef?: MutableRefObject<GridApi>
  ) => Promise<QueryResults<T>>;
  disableColumnFilter?: boolean;
  enableAdvancedSearch?: boolean;
  enableCreate?: boolean;
  /** When set, the + button opens this menu instead of navigating to "create". */
  createMenu?: CreateMenuSection[];
  /** When set, the toolbar + button calls this instead of navigating to "create". */
  onCreate?: () => void;
  enableDelete?: boolean;
  enableFilter?: boolean;
  /** When true, show the Display preferences button in the toolbar. */
  enableDisplayPreference?: boolean;
  /**
   * When true, show Find… (SearchField) without requiring SEARCH / advanced search.
   * `enableAdvancedSearch` also shows Find.
   */
  enableFind?: boolean;
  /**
   * Read-only selection notify. Fired with the selected row objects.
   * Do not pass `rowSelectionModel` / `onRowSelectionModelChange` — they break ACTIONS.
   */
  onSelectionChange?: (rows: T[]) => void;
  /** Bump to clear the checkbox selection from outside the grid. */
  clearSelectionToken?: number | string;
  /**
   * A TREE grid's selection (BP-109): turns the user's tick into the selection it
   * means — ticking a parent ticks its subtree. Called with the selection MUI
   * proposes and the one before it; the ids it returns become the selection. They
   * may be rows the grid is not showing (a closed parent's lines): those stay
   * selected and show ticked when their rows appear.
   */
  selectionCascade?: (next: GridRowId[], previous: GridRowId[]) => GridRowId[];
  /** Every selected id, shown or not (onSelectionChange reports the rows on screen). */
  onSelectionIdsChange?: (ids: GridRowId[]) => void;
  /**
   * Removes the header Select All (Oracle ADF `multipleNoSelectAll`): a tree grid's
   * header cannot say what "all" is. Row ticks stay.
   */
  disableSelectAll?: boolean;
  exportFileName?: string;
  /** Data Export view identifier. Omit to retain the native page-only export. */
  exportView?: string;
  /** Static filters required by a specific export view. */
  exportExtraFilters?: Record<string, unknown>;
  initialSort?: GridSortModel;
  name: string;
  onCellClick?: GridEventListener<"cellClick">;
  onDelete?: (GridRowSelectionModel) => void;
  onRowClick?: GridEventListener<"rowClick">;
  setSelected?: (selected?: T) => void;
  sx?: SxProps<Theme>;
  quickSearchItems?: GridFilterItem[];
  /** When false, do not sync quick filter into search field state (toolbar search box). Filter still applied via filterObj. Default true. */
  quickSearchValueDisplay?: boolean;
  /** Optional trailing toolbar slot (after the flex spacer, before SEARCH / Display). */
  toolbarExtras?: React.ReactNode;
  /** A row under the toolbar, above the column headers (PremiumListToolbar's `toolbarBelow`). */
  toolbarBelow?: React.ReactNode;
  /**
   * Module-level entries for the grid's settings ⋮, in its "Settings" section after
   * "Display" — the list grid's `settingsMenuExtras` slot (PATTERNS), on the detail-tab
   * toolbar's ⋮ (H233). Each item should accept and call `hideMenu`. Omit it and the
   * ⋮ offers Export (CSV, Print) and Settings → Display only.
   */
  settingsMenuExtras?: React.ReactNode;
  /** Replaces the grid footer. Use this for extra summary rows above pagination. */
  footer?: JSXElementConstructor<any>;
  /** Empty-grid message. Defaults to "No rows". */
  noRowsLabel?: string;
  /**
   * The caller's handle on THIS grid's API, for what a row event does not carry in
   * Premium 7 (the row node behind a click: the Onboarding Checklist's phase bands).
   * Omit it and the grid keeps its own. The grid always uses the one ref, so its own
   * filter, Find and export wiring are unchanged.
   */
  apiRef?: MutableRefObject<GridApi>;
  [x: string | number | symbol]: unknown;
  isHeaderBorder?: boolean;
  /**
   * Where the grid sits. "detailTab" (the default): inside a record's tab panel, the
   * toolbar flush. "list": a module's LIST screen, on the listing gutter and the list
   * toolbar spacing like every Pro AeronetDataGrid list (listingPremiumGridSx).
   */
  layout?: "detailTab" | "list";
}

/**
 * A tree grid's columns with the checkbox column's header Select All removed (BP-109;
 * Oracle ADF `multipleNoSelectAll`): row ticks stay, the header cannot say what "all"
 * is. The ONE implementation: AeronetDataGridPremium's `disableSelectAll` uses it, and a
 * raw MUI DataGridPremium (the aircraft Components tab) passes its columns through it
 * with `checkboxSelection` on. A caller's own checkbox column keeps its settings AND its
 * place (the Components tab puts the reveal chevron first, then the tick — the app-wide
 * order); only the header goes. With no checkbox column of the caller's, one is put
 * first, where MUI puts its own.
 */
export function withoutSelectAll<T extends GridValidRowModel>(columns: GridColDef<T>[]): GridColDef<T>[] {
  const noHeader = (own?: GridColDef<T>) =>
    ({ ...GRID_CHECKBOX_SELECTION_COL_DEF, ...own, renderHeader: () => null }) as GridColDef<T>;
  return columns.some((c) => c.field === GRID_CHECKBOX_SELECTION_FIELD)
    ? columns.map((c) => (c.field === GRID_CHECKBOX_SELECTION_FIELD ? noHeader(c) : c))
    : [noHeader(), ...columns];
}

export function AeronetDataGridPremium<T extends GridValidRowModel>(
  props: AeronetDataGridPremiumProps<T>
) {
  const {
    actions,
    advancedSearchForm = AdvancedSearchForm2,
    checkboxSelection = true,
    children,
    columns,
    components = { toolbar: PremiumListToolbar },
    currentUserId,
    data,
    disableColumnFilter = true,
    enableAdvancedSearch = false,
    enableCreate = true,
    createMenu,
    onCreate,
    enableDelete = true,
    enableFilter = false,
    enableDisplayPreference = false,
    enableFind = false,
    onSelectionChange: onSelectionChangeProp,
    clearSelectionToken,
    selectionCascade,
    onSelectionIdsChange,
    disableSelectAll = false,
    exportFileName = "Aeronet",
    exportView,
    exportExtraFilters,
    initialSort,
    name,
    onDelete,
    quickSearchItems,
    quickSearchValueDisplay,
    toolbarExtras,
    toolbarBelow,
    settingsMenuExtras,
    footer,
    noRowsLabel = "No rows",
    apiRef: callerApiRef,
    setSelected,
    sx = [],
    isHeaderBorder = false,
    layout = "detailTab",
    ...dataGridProps
  } = props;

  // Callers must not control selection — it blinds ACTIONS. Strip + warn.
  const {
    rowSelectionModel: _callerRowSelectionModel,
    onRowSelectionModelChange: _callerOnRowSelectionModelChange,
    ...safeDataGridProps
  } = dataGridProps as typeof dataGridProps & {
    rowSelectionModel?: unknown;
    onRowSelectionModelChange?: unknown;
  };

  const { items: defaultItems, ...advancedSearchProps } =
    props.advancedSearchProps ?? {};

  const [action, setAction] = useState<ActionType>(Actions.None);
  const [filter, setFilter] = useState<GridFilterModel | undefined>();
  const [filterVisibleModel, setFilterVisibleModel] = useState<GridFilterModel | undefined>();
  const [loading, setLoading] = useState(true);
  // TODO: Detail views do not wait on saved filters, sort, or display prefs.
  // Set this back to false and restore the load effect when list screens use this grid.
  const [prefsReady] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [rowCount, setRowCount] = useState(-1);
  const [searchText, setSearchText] = useState("");
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  // Seeded from `initialSort` so the consumer's default order is what the FIRST
  // fetch asks for — the list grid's H137d fix, carried over. It used to start
  // undefined, so initialSort only applied after a user cleared a header sort.
  // Seeded once (useState initializer): an inline-literal initialSort prop
  // cannot re-trigger a fetch. A user's header sort still replaces it, and
  // clearing that sort still returns to it (onSortChange).
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    initialSort
  );
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [filterButtonEl, setFilterButtonEl] =
    useState<HTMLButtonElement | null>(null);
  const [buttonText, setButtonText] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [, setSearchField] = useState("");
  const [filterObj, setFilterObj] = useState<GridFilterItem>();
  const [filterItems, setFilterItems] = useState<GridFilterItem[]>([]);
  const [advanceSearchClicked, setAdvanceSearchClicked] = useState(false);
  const {
    // TODO: Do not read or write stored filters / pagination on detail views.
    // advanceSearch,
    // setAdvanceSearch,
    advanceSearchTriggered,
    setAdvanceSearchTriggered,
    // pagination,
    // setPagination,
    // quickFilter,
    // setQuickFilter,
    setQuickSearchField,
    resetQuickFilter,
    setQuickSearchValue,
    setFilterActive,
    filterActive
  } = useDataGridStore();
  // load the query params

  const ownApiRef = useGridApiRef();
  const apiRef = (callerApiRef ?? ownApiRef) as typeof ownApiRef;

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      return;
    }
    if (
      _callerRowSelectionModel !== undefined ||
      _callerOnRowSelectionModelChange !== undefined
    ) {
      console.warn(
        "AeronetDataGridPremium: do not pass rowSelectionModel or onRowSelectionModelChange — they replace internal selection and break ACTIONS. Use onSelectionChange and clearSelectionToken instead."
      );
    }
  }, [_callerRowSelectionModel, _callerOnRowSelectionModelChange]);

  // TODO: Detail views should not keep a saved grid state in localStorage.
  // window.addEventListener("beforeunload", () => {
  //   const localStorageKey = getLocalStorageKey(currentUserId);
  //   localStorage.removeItem(localStorageKey);
  // });

  const getLocalStorageKey = useCallback(
    (userId) => {
      return `aeronet-${name}-state-${userId}`;
    },
    [name]
  );
  // useEffect(() => {
  //   if (quickFilter.hasOwnProperty(name)) {
  //     setFilterObj(quickFilter[name]);
  //     if (quickSearchValueDisplay) {
  //       setSearchText(quickFilter[name].value);
  //       setQuickSearchField(quickFilter[name].field);
  //       setOperator(quickFilter[name].operator);
  //       setSearchField(quickFilter[name].field);
  //     }
  //   }
  // }, [
  //   quickFilter,
  //   name,
  //   quickSearchValueDisplay,
  //   setFilterObj,
  //   setSearchText,
  //   setQuickSearchField,
  //   setOperator,
  //   apiRef,
  // ]);
  /**
   * Combines the actions prop with default Actions.
   * Flattens ActionMenuSubheader children so their action types are registered.
   */
  useMemo(() => {
    console.debug("Actions memo");
    if (actions && actions.length > 0) {
      let additionalActions = {};
      for (const entry of actions) {
        if ("subheader" in entry && "children" in entry) {
          for (const child of (entry as ActionMenuSubheader).children) {
            if (child.action) {
              additionalActions = {
                ...additionalActions,
                ...Object.fromEntries([child.action]),
              };
            }
          }
        } else {
          const itemAction = (entry as ActionMenuItem).action;
          if (itemAction) {
            additionalActions = {
              ...additionalActions,
              ...Object.fromEntries([itemAction]),
            };
          }
        }
      }
      // Same Actions object the shared menu reads. Imports cannot be reassigned.
      Object.assign(Actions, additionalActions);
    }
  }, [actions]);

  const exportState = useCallback(() => {
    // TODO: Detail views should not save column layout, density, or pagination.
    // Re-enable UserDisplayPreference(state, name) for list screens.
    // if (name) {
    //   let state = apiRef.current.exportState();
    //   const density = gridDensitySelector(
    //     apiRef.current.state,
    //     apiRef.current.instanceId
    //   );
    //   state.density = density;
    //   UserDisplayPreference(state, name);
    // }
  }, []);

  // TODO: Detail views should not restore saved filters, sort, or display
  // preferences. Re-enable initialColumnVisibilityDefaultsRef and the
  // loadSavedView effect below when this grid is used on list screens.
  //
  // const initialColumnVisibilityDefaultsRef = useRef<Record<string, boolean>>(
  //   (dataGridProps as {
  //     initialState?: {
  //       columns?: {
  //         columnVisibilityModel?: Record<string, boolean>;
  //       };
  //     };
  //   })?.initialState?.columns?.columnVisibilityModel || {}
  // );
  //
  // useEffect(() => {
  //   if (!name) {
  //     setPrefsReady(true);
  //     return;
  //   }
  //   setPrefsReady(false);
  //   let cancelled = false;
  //   async function loadSavedView() {
  //     const storedAdvancedSearch = advanceSearch.hasOwnProperty(name)
  //       ? advanceSearch[name]
  //       : [];
  //     const hasStoredAdvancedSearch = storedAdvancedSearch.length > 0;
  //     const [filterResponse, sortResponse, displayResponse] = await Promise.all([
  //       hasStoredAdvancedSearch
  //         ? Promise.resolve(null)
  //         : FilterGetApi(name).catch(() => null),
  //       UserSortPreferenceGet(name).catch(() => null),
  //       UserDisplayPreferenceGet(name).catch(() => null),
  //     ]);
  //     if (cancelled) {
  //       return;
  //     }
  //     if (displayResponse && displayResponse.json_string) {
  //       const displayPreference = JSON.parse(displayResponse.json_string);
  //       const {
  //         density,
  //         preferencePanel,
  //         pagination: savedPagination,
  //         ...state
  //       } = displayPreference;
  //       delete state.filter;
  //       delete state.sorting;
  //       delete state.preferencePanel;
  //       delete state.preferencesPanel;
  //       delete state.pagination;
  //       void preferencePanel;
  //       state.columns = state.columns || {};
  //       state.columns.columnVisibilityModel = {
  //         ...initialColumnVisibilityDefaultsRef.current,
  //         ...(state.columns.columnVisibilityModel || {}),
  //       };
  //       const densityValue =
  //         typeof density === "string" ? density : density?.value;
  //       if (
  //         densityValue === "compact" ||
  //         densityValue === "standard" ||
  //         densityValue === "comfortable"
  //       ) {
  //         apiRef.current.setDensity(densityValue);
  //       }
  //       const savedModel = savedPagination?.paginationModel;
  //       if (savedModel) {
  //         const savedPage = Number(savedModel.page);
  //         const savedPageSize = Number(savedModel.pageSize);
  //         const pageValue = Number.isFinite(savedPage) ? savedPage : 0;
  //         const pageSizeValue = Number.isFinite(savedPageSize) ? savedPageSize : 15;
  //         setPagination(name, {
  //           page: pageValue,
  //           pageSize: pageSizeValue,
  //         });
  //         setPage(pageValue);
  //         setPageSize(pageSizeValue);
  //       }
  //       apiRef.current.restoreState(state);
  //     }
  //     if (sortResponse && sortResponse.json_string) {
  //       setSortModel(JSON.parse(sortResponse.json_string));
  //     }
  //     if (hasStoredAdvancedSearch) {
  //       setAdvanceSearchClicked(true);
  //       setAdvanceSearchTriggered(true);
  //       const items = storedAdvancedSearch;
  //       setFilter({ items });
  //       setFilterVisibleModel({ items });
  //     } else if (filterResponse && filterResponse.json_string) {
  //       const filterItem = JSON.parse(filterResponse.json_string);
  //       setFilterItems(filterItem);
  //       setFilterActive(true);
  //       setFilter({ items: filterItem });
  //       setFilterVisibleModel({ items: filterItem });
  //     }
  //     setPrefsReady(true);
  //   }
  //   loadSavedView();
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [
  //   name,
  //   apiRef,
  //   advanceSearch,
  //   setAdvanceSearchTriggered,
  //   setAdvanceSearchClicked,
  //   setFilterActive,
  //   setPagination,
  // ]);

  const defaultFilters = useMemo(() => {
    let filter: GridFilterModel = {
      items: [],
    };

    if (defaultItems && defaultItems.length > 0) {
      // deep copy defaultItems so filters can be restored when using back button
      filter.items = defaultItems.map((obj) => ({ ...obj }));
    }
    return filter;
  }, [defaultItems]);

  /**
   * Sets the text in the quick search from the 'q' query parameter
   */
  

  /**
   * Sets the filter model from:
   * - advancedSearchProps.items
   * - 'filter' query parameters
   */


  // TODO: Detail views should not restore a stored page or page size.
  // useEffect(() => {
  //   const storedPage =
  //     pagination.hasOwnProperty(name) && pagination[name]
  //       ? Number(pagination[name].page)
  //       : 0;
  //   const nextPage = Number.isFinite(storedPage) ? storedPage : 0;
  //   setPage((current) => (current === nextPage ? current : nextPage));
  // }, [pagination, name]);
  //
  // useEffect(() => {
  //   const storedPageSize =
  //     pagination.hasOwnProperty(name) && pagination[name]
  //       ? Number(pagination[name].pageSize)
  //       : 15;
  //   const nextPageSize = Number.isFinite(storedPageSize) ? storedPageSize : 15;
  //   setPageSize((current) =>
  //     current === nextPageSize ? current : nextPageSize
  //   );
  // }, [pagination, name]);

  /** Stable identity when page/pageSize unchanged — avoids MUI pagination sync + onPaginationModelChange on unrelated re-renders (e.g. slide-in opening). */
  const paginationModel = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  // A row-grouped list (rowGroupingModel) always sorts by its grouping fields
  // first, so every band arrives whole and in order, however the user sorts.
  // They lead the REQUEST's sort only, never the grid's sortModel: a hidden
  // grouping field in sortModel put MUI's multi-sort badge on a visible header
  // (the Onboarding Checklist's "# ↑ 2", Aaron 2026-10-21). A string key, so an
  // inline-literal rowGroupingModel doesn't rebuild the params every render.
  const groupingSortKey = (
    ((dataGridProps as { rowGroupingModel?: string[] }).rowGroupingModel ?? []) as string[]
  ).join(",");

  /** Shared filter/sort param builder (no page/size). */
  const buildFilterSortParams = useCallback(() => {
    const params: { [key: string]: any } = {};
    const filters: string[] = [];

    if (filter) {
      for (const f of filter.items) {
        if (f.field) {
          if (f.field === "q") {
            params["q"] = f.value;
            continue;
          }

          if (isAppliedFilterItem(f)) {
            let op = f.operator || "contains";
            let value = f.value;
            if (op === "contains") {
              op = "ilike";
              value = "%" + f.value + "%";
            } else if (op === "startsWith") {
              op = "ilike";
              value = f.value + "%";
            } else if (op === "endsWith") {
              op = "ilike";
              value = "%" + f.value;
            }
            if (f.field.includes(".")) {
              const parts = f.field.split(".", -2);
              let [model, field] = parts.slice(-2);
              model = model[0].toUpperCase() + model.slice(1);
              filters.push(
                JSON.stringify({
                  model: model,
                  field: field,
                  op: op,
                  value: value,
                })
              );
            } else {
              filters.push(
                JSON.stringify({
                  field: f.field,
                  op: op,
                  value: value,
                })
              );
            }
          }
        }
      }
    }
    if (filters.length > 0) {
      params["filter"] = filters;
    }

    const sortFields: string[] = [];
    const userSorted = new Set((sortModel ?? []).map((item) => item.field));
    for (const field of groupingSortKey ? groupingSortKey.split(",") : []) {
      if (!userSorted.has(field)) sortFields.push(field);
    }
    if (sortModel) {
      for (const sortItem of sortModel) {
        let field = sortItem.field;
        if (sortItem.sort && sortItem.sort === "desc") {
          field = "-" + field;
        }
        sortFields.push(field);
      }
    }
    if (sortFields.length > 0) {
      params["sort"] = sortFields.toString();
    }
    return params;
  }, [filter, sortModel, groupingSortKey]);

  /** Fetch one server page at a time. */
  useEffect(() => {
    const controller = new AbortController();

    async function getData() {
      setLoading(true);
      try {
        const results: QueryResults<T> = await data(
          {
            ...buildFilterSortParams(),
            page: page + 1,
            size: pageSize,
          },
          {
            signal: controller.signal,
            cache: false,
          },
          apiRef
        );
        setRowCount(results.total);
        setRows(results.rows);
        setLoading(false);
      } catch (e) {
        if (controller.signal.aborted) {
          return;
        }
        setRowCount(0);
        setLoading(false);
      }
    }

    if (currentUserId && prefsReady) {
      getData();
    }

    return () => {
      controller.abort();
    };
  }, [
    apiRef,
    buildFilterSortParams,
    currentUserId,
    data,
    page,
    pageSize,
    prefsReady,
  ]);

  useEffect(() => {
    const handleClick = (event) => {
      if (event.target.id === "action") {
        setButtonText("action");
      } else {
        setButtonText("");
      }
    };

    window.addEventListener("click", handleClick); // Log all clicks

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
      console.debug("onFilterChange");
      // we're hijacking the restoreState reason to also mean loading filter from query params
      // in this case we only want to set the filter we don't want to reset the query params and
      // navigate again
      filterModel.items = filterModel.items
        .filter((obj) => obj.value !== null && obj.value !== "")
        .map((obj) => ({ ...obj }));
      console.log(filterModel,"compare ",filter)
      // Reset to first page when the filter set changes — but not for programmatic
      // restoreState (e.g. closing advanced-search slide), which would otherwise
      // force page 0 and cascade into onPaginationModelChange / store updates.
      if (
        details.reason !== "restoreState" &&
        filter &&
        filter?.items !== filterItems
      ) {
        apiRef.current.setPage(0);
      }

      if (details.reason === "restoreState") {
        console.debug("Filters: ");
        console.dir(filterModel);
        setFilter(filterModel);
        setFilterVisibleModel(filterModel);
        setSearchField("");
        setSearchText("");
      } 
      
      else if (buttonText.length > 0) {
        console.log("here action is triggered");
      } 
      else {
        console.debug("navigating with filter");
        //for Filter and quick search combined but need to save only filter
        if (filterObj) {
          let newFilter = filterModel?.items.filter((item: GridFilterItem) => {
            return item.field !== filterObj.field;
          });
          if (newFilter.length > 0) {
            let saveFilter = newFilter?.filter((item: GridFilterItem) => {
              return !["", null, undefined].includes(item.value);
            });
            if (
              saveFilter.length > 0 &&
              
              !advanceSearchTriggered
            ) {

              // TODO: Detail views should not save filters.
              // FilterApi(saveFilter, name);
            }
          }
        } else {
          //To Save filter
          let newFilter = filterModel?.items.filter((item: GridFilterItem) => {
            return !["", null, undefined].includes(item.value);
          });
          if (
            newFilter.length > 0 &&
            
            !advanceSearchTriggered
          ) {
            // TODO: Detail views should not save filters.
            // FilterApi(newFilter, name);
          }
        }

        // the logic toinclude quick search in filter model when both applied
        if(filterObj){
          const existingFilterItem = filterModel.items.find((item) => item.field === filterObj?.field && item.value ===undefined);
          if(existingFilterItem){

            setFilter(filterModel);
          }
          else{
            const existingFilter = filterModel.items.find((item) => item.field === filterObj.field && item.value !==undefined);
            if(existingFilter){
              setFilter(filterModel);
            }
            else{
              
              setFilter({items:[...filterModel.items,filterObj]});
            }
          }
        }
        else{
          setFilter(filterModel);
        }

        //logic to display only filter panel field not the quick filter in filter panel
        let newFilter = filterModel?.items.filter((item: GridFilterItem) => {
            return item.field !== filterObj?.field;
          });
        if (newFilter) {
          
          setFilterVisibleModel({items:[...newFilter]});
        }
        else{
          setFilterVisibleModel(filterModel);
        }
      }

      // Only when user clears all filters from the filter panel — not when reason is
      // undefined (MUI uses that for internal sync, e.g. showFilterPanel without reason).
      if (String(details.reason) === "removeAllFilterItems" || !details.reason) {
        // TODO: Detail views should not delete stored filters.
        // FilterDelete(name);
        setFilterItems([]);
        setFilter(filterModel);
        setFilterVisibleModel(filterModel);
        setSearchField("q");
        setSearchText("");
        setQuickSearchField("q");
        setQuickSearchValue("");
        setFilterActive(false);
      }
    },
    [
      buttonText,
      filterObj,
      setFilter,
      filterItems,
      advanceSearchTriggered,
      setQuickSearchField,
      setQuickSearchValue,
      setFilterActive,
      setFilterVisibleModel,
      apiRef,
      filter
    ]
  );

  const onSelectionChange = useCallback(
    (proposed: GridRowSelectionModel) => {
      // A tree grid's cascade (BP-109) turns the tick into the selection it means.
      const newSelectionModel = selectionCascade
        ? selectionCascade([...proposed], [...selectionModel])
        : proposed;
      setSelectionModel(newSelectionModel);
      if (onSelectionChangeProp) {
        const idSet = new Set(newSelectionModel);
        const selected = (rows as T[]).filter((row) =>
          idSet.has((row as GridValidRowModel).id as string | number)
        );
        onSelectionChangeProp(selected);
      }
      onSelectionIdsChange?.([...newSelectionModel]);
    },
    [onSelectionChangeProp, onSelectionIdsChange, rows, selectionCascade, selectionModel]
  );

  // disableSelectAll: the checkbox column without its header Select All (withoutSelectAll).
  const gridColumns = useMemo(
    () => (disableSelectAll && checkboxSelection ? withoutSelectAll(columns) : columns),
    [columns, disableSelectAll, checkboxSelection]
  );

  const clearSelectionTokenRef = useRef(clearSelectionToken);
  useEffect(() => {
    if (clearSelectionTokenRef.current === clearSelectionToken) {
      return;
    }
    clearSelectionTokenRef.current = clearSelectionToken;
    if (clearSelectionToken === undefined) {
      return;
    }
    setSelectionModel([]);
    onSelectionChangeProp?.([]);
    onSelectionIdsChange?.([]);
  }, [clearSelectionToken, onSelectionChangeProp, onSelectionIdsChange]);

  const onSortChange = useCallback(
    (sortModel: GridSortModel, details: GridCallbackDetails<any>) => {
      console.debug("onSortChange");
      console.debug(details);
     
      if (sortModel && sortModel.length > 0) {
      // TODO: Detail views should not save the sort preference.
      // UserSortPreference(sortModel, name);
      setSortModel(sortModel);
      
    } else {
      if (initialSort) {
        setSortModel(initialSort);
        
      }
    }
     
    },
    [initialSort]
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
      const nextPage = Number(model.page);
      const nextPageSize = Number(model.pageSize);
      if (
        (Number.isFinite(nextPage) ? nextPage : 0) === page &&
        (Number.isFinite(nextPageSize) ? nextPageSize : pageSize) === pageSize
      ) {
        return;
      }
      console.debug("handlePaginationModelChange");
      const pageToStore = Number.isFinite(nextPage) ? nextPage : 0;
      const pageSizeToStore = Number.isFinite(nextPageSize) ? nextPageSize : 15;
      // TODO: Detail views should not store page, page size, or display prefs.
      // const state = apiRef.current.exportState();
      // const density = gridDensitySelector(apiRef.current.state, apiRef.current.instanceId);
      // setPagination(name, { page: pageToStore, pageSize: pageSizeToStore });
      // UserDisplayPreference({ ...state, density, pagination: { paginationModel: { page: pageToStore, pageSize: pageSizeToStore } } }, name);
      setPage(pageToStore);
      setPageSize(pageSizeToStore);
    },
    [page, pageSize, setPageSize, setPage]
  );
  const onClose = useCallback(() => {
    setAction(Actions.None);
    setSelectionModel([]);
    if (setSelected) {
      setSelected(undefined);
    }
  }, [setSelected]);

  /**
   * Clear the quick search field and the query parameters
   */
  const onClearSearch = useCallback(() => {
    setSearchText("");

    // to remove quick search on cancel button from filter list
    if (filterObj) {
      setSearchField("");
      setQuickSearchField("q")
      setQuickSearchValue("")
      setFilterObj(undefined);
      resetQuickFilter();
      
      //to apply the other filter by removing quick search from filter model
      let newFilter = filter?.items.filter((item: GridFilterItem) => {
        return item.field !== filterObj.field;
      });
      if (newFilter) {
        apiRef.current.setFilterModel({items:newFilter});
      }

      // to reset quick search on clear button
    }
  }, [filterObj, filter, apiRef,resetQuickFilter,setQuickSearchField,setQuickSearchValue]);

  const onClearAdvancedSearch = useCallback(() => {
    if (filterObj) {
      let newFilter = filter?.items.filter((item: GridFilterItem) => {
        return item.field !== filterObj.field;
      });
      if (newFilter) {
        apiRef.current.upsertFilterItems(newFilter);
      }

      setSearchField("");
      setQuickSearchField("q")
      setFilterObj(undefined);
    } else {
      apiRef.current.setFilterModel(defaultFilters, "restoreState");
    }
  }, [
    apiRef,
    defaultFilters,
    filterObj,
    filter,
    setQuickSearchField,
  ]);

  const onCloseAdvancedSearch = useCallback(() => {
    setAction(Actions.None);
  }, []);
  
  const onCloseAdvancedSearchSlide = useCallback(() => {
    setAction(Actions.None);
    setAdvanceSearchClicked(false);
    setAdvanceSearchTriggered(false);
    // TODO: Detail views should not store advanced-search filters.
    // setAdvanceSearch(name, []);
    apiRef.current.setFilterModel(defaultFilters, "restoreState");
  }, [setAdvanceSearchClicked,setAdvanceSearchTriggered,defaultFilters,apiRef]);

  const onCloseAdvancedSearchIcon = useCallback(() => {
    setAction(Actions.None);
    setAdvanceSearchClicked(false);
    setAdvanceSearchTriggered(false);
    // TODO: Detail views should not store advanced-search filters.
    // setAdvanceSearch(name, []);
    apiRef.current.setFilterModel(defaultFilters, "restoreState");
  }, [setAdvanceSearchClicked,setAdvanceSearchTriggered,defaultFilters,apiRef]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchText);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchText]);

  const quickSearchField = filterObj?.field;
  const quickSearchOperator = filterObj?.operator;

  useEffect(() => {
    if (!quickSearchField || !quickSearchOperator) {
      return;
    }

    const filterModel = gridFilterModelSelector(apiRef);
    const existingFilterItem = filterModel.items.find(
      (item) => item.field === quickSearchField && item.value !== undefined
    );

    const isEmpty =
      debouncedValue === undefined ||
      debouncedValue === "" ||
      (Array.isArray(debouncedValue) && debouncedValue.length === 0);

    if (isEmpty) {
      if (existingFilterItem) {
        apiRef.current.deleteFilterItem(existingFilterItem);
      }
      return;
    }

    if (existingFilterItem) {
      apiRef.current.deleteFilterItem(existingFilterItem);
    }

    apiRef.current.upsertFilterItem({
      field: quickSearchField,
      operator: quickSearchOperator,
      value: debouncedValue,
    });
  }, [debouncedValue, quickSearchField, quickSearchOperator, apiRef]);

  return (
    <Box display="flex" height="100%" maxWidth="100%">
      <Box flexGrow={1} maxWidth="100%">
        {/* @ts-ignore */}
        <DataGridPremium
          apiRef={apiRef}
          autoHeight
          columnHeaderHeight={52}
          checkboxSelection={checkboxSelection}
          columns={gridColumns as GridColDef[]}
          // A cascade may select rows not on screen (a closed parent's lines):
          // keep them selected while the row set changes.
          keepNonExistentRowsSelected={Boolean(selectionCascade)}
          slots={{
            // THE Filters panel, Figma Component 73 (filterPanel.tsx); a consumer's own wins.
            filterPanel: CustomFilterPanelPremium,
            ...components,
            toolbar: components.toolbar as any,
            ...(footer ? { footer } : {}),
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Box sx={{ textAlign: "center" }}>
                  {(filterObj || filterActive)
                    ? "No results were found within the applied filter"
                    : advanceSearchTriggered
                      ? "No results were found"
                      : noRowsLabel}
                </Box>
              </Box>
            ),
          }}
          slotProps={{
            toolbar: {
              toolbarExtras,
              toolbarBelow,
              settingsMenuExtras,
              name:name,
              action: action,
              actions: actions,
              clearSearch: onClearSearch,
              defaultItems: defaultItems,
              enableAdvancedSearch: enableAdvancedSearch,
              enableFind: enableFind,
              enableCreate: enableCreate,
              createMenu: createMenu,
              onCreate: onCreate,
              enableDelete: enableDelete,
              enableFilter: enableFilter,
              enableDisplayPreference: enableDisplayPreference,
              exportState: exportState,
              // @ts-ignore
              onChange: (event, searchFieldText, filterObj) => {
                setSearchField(searchFieldText);
                setSearchText(event.target.value);
                setFilterObj(filterObj);
              },
              setAction: setAction,
              setSelected: setSelected,
              setSelectionModel: onSelectionChange,
              value:searchText,
              selectionModel: selectionModel,
              onDelete: onDelete,
              printOptions: {
                hideFooter: true,
                hideToolbar: true,
                fileName: exportFileName,
              },
              csvOptions: {
                fileName: exportFileName,
              },
              quickSearchItems: quickSearchItems,
              setFilterButtonEl,
              setAdvanceSearchClicked: setAdvanceSearchClicked,
              advanceSearchClicked: advanceSearchClicked,
              getLocalStorageKey: getLocalStorageKey,
              currentUserId: currentUserId,
              setFilterObj:setFilterObj,
              exportView,
              estimatedRows: rowCount,
              exportFilterModel: filter,
              exportSortModel: sortModel,
              exportSearch: searchText,
              exportExtraFilters,
              exportFileName,
            },
            panel: {
              anchorEl: filterButtonEl,
            },
            // THE Filters panel's shared defaults (filterPanel.tsx).
            filterPanel: FILTER_PANEL_PROPS,
          }}
          filterModel={filterVisibleModel}
          // FILTERS in the toolbar (enableFilter) needs the column filter the
          // detail-tab default turns off — MUI renders no filter button without it.
          disableColumnFilter={disableColumnFilter && !enableFilter}
          disableColumnSelector={true}
          disableRowSelectionOnClick
          filterMode="server"
          loading={loading || !prefsReady}
          onFilterModelChange={onFilterChange}
          onRowSelectionModelChange={onSelectionChange}
          onSortModelChange={onSortChange}
          pagination
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={rowCount}
          rows={rows}
          pageSizeOptions={[15, 50, 100]}
          rowSelectionModel={selectionModel}
          sortModel={sortModel}
          sx={[
            commonDataGridPremiumSx,
            // Buttons sit in the toolbar, so the Misc 20px gap is padding under
            // the toolbar rather than margin above the whole grid (the shared
            // detail-tab spacing, also used by raw detail-tab grids).
            // A detail tab sits in its record's panel; a LIST screen takes the
            // Pro list's gutter and toolbar (listingPremiumGridSx).
            layout === "list" ? listingPremiumGridSx : detailTabGridSx,
            {
              // First load only. Once rows are on screen, leave them in place
              // and let the default overlay draw the spinner on top, the same
              // way the Pro grid does while the next page is fetching.
              ...((loading || !prefsReady) && rows.length === 0
                ? {
                    "--DataGrid-overlayHeight": "220px",
                    "& .MuiDataGrid-virtualScroller": {
                      minHeight: 220,
                    },
                  }
                : {}),
              "& .MuiDataGrid-withBorderColor": !isHeaderBorder
                ? {
                    borderTop: "0px !important",
                  }
                : {},
              "& .MuiDataGrid-toolbarContainer": {
                alignItems: "center",
                gap: 0,
              },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...safeDataGridProps}
        />
        <Suspense fallback={<LoadingSpinner />}>
          {enableAdvancedSearch &&
          advancedSearchForm &&
          filter &&
          defaultItems &&
          action === Actions.AdvancedSearch ? (
            <AdvancedSearch2
              open={true}
              defaultItems={defaultItems}
              onClear={onClearAdvancedSearch}
              onClose={onCloseAdvancedSearch}
              apiRef={apiRef as MutableRefObject<any>}
              name={name}
              form={advancedSearchForm}
              onCloseAdvancedSearchIcon ={onCloseAdvancedSearchIcon}
              onCloseAdvancedSearchSlide={onCloseAdvancedSearchSlide}
              {...(advancedSearchProps as any)}
            />
          ) : (
            <></>
          )}
          {children ? children({ onClose, action, Actions }) : <></>}
        </Suspense>
      </Box>
    </Box>
  );
}

export default AeronetDataGridPremium;
